import { useEffect, useState } from "react"
import { createRootRouteWithContext, Outlet, useRouterState } from "@tanstack/react-router"
import { useConvexAuth, useQuery } from "convex/react"
import { PostHogProvider } from "@posthog/react"
import { api } from "../../convex/_generated/api"
import { Toaster } from "@/components/ui/sonner"
import { SiteHeader } from "@/components/site-header"
import { MarketingHeader } from "@/components/marketing-header"
import { CandidateHeader } from "@/features/candidate"
import {
  EmployerHeader,
  useCompanyChromeCapabilities,
} from "@/features/company"
import { AppShellFonts } from "@/components/app-shell-fonts"
import { ChromePlaceholder } from "@/components/chrome-placeholder"
import {
  chromeDependsOnRole,
  readCachedChromeRole,
  usesAdminChrome,
  usesAppChrome,
  usesCandidateChrome,
  usesEmployerChrome,
  usesMarketingChrome,
  writeCachedChromeRole,
  type ChromeRole,
} from "@/lib/marketing-chrome"
import { AdminHeader } from "@/components/admin-header"
import { CompanyCapabilitiesProvider } from "@/lib/company-capabilities"
import {
  identifyPostHogUser,
  posthogClient,
  setPostHogCompanyGroup,
} from "@/lib/posthog"
import { authClient } from "@/lib/auth-client"

export type RouterContext = {
  // reserved for future auth context
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
})

function useChromeRole(
  profile: { role: ChromeRole } | null | undefined,
): ChromeRole | null {
  // Seed from sessionStorage so the first paint after refresh matches last visit.
  const [cachedRole, setCachedRole] = useState<ChromeRole | null>(() =>
    readCachedChromeRole(),
  )

  useEffect(() => {
    if (profile === undefined) return
    if (profile?.role === "seeker" || profile?.role === "employer") {
      writeCachedChromeRole(profile.role)
      setCachedRole(profile.role)
      return
    }
    // Signed out or no role yet — clear cache so marketing chrome can show.
    writeCachedChromeRole(null)
    setCachedRole(null)
  }, [profile])

  if (profile?.role === "seeker" || profile?.role === "employer") {
    return profile.role
  }
  // Profile still loading: keep last known role to avoid shell flash.
  if (profile === undefined) return cachedRole
  return null
}

function RootLayout() {
  const posthog = posthogClient
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const { isLoading: convexAuthLoading } = useConvexAuth()
  const { data: session } = authClient.useSession()
  const profile = useQuery(api.profiles.me)
  const employerCompany = useQuery(
    api.companies.myCompany,
    profile?.role === "employer" ? {} : "skip",
  )

  useEffect(() => {
    const user = session?.user
    if (!user?.id) return

    identifyPostHogUser({
      distinctId: user.id,
      email: user.email,
      name: user.name,
      role: profile?.role,
      portalStatus: profile ? "member" : session?.user ? "lead" : undefined,
      candidateOnboarded: profile?.candidateOnboardingAt != null,
      signupStage: profile
        ? profile.role === "seeker" && !profile.candidateOnboardingAt
          ? "role_selected"
          : "profile_started"
        : "account_created",
    })
  }, [session?.user, profile?.role, profile?.candidateOnboardingAt, profile])

  useEffect(() => {
    setPostHogCompanyGroup(
      profile?.role === "employer" ? employerCompany?._id : null,
    )
  }, [profile?.role, employerCompany?._id])
  const companyCapabilities = useCompanyChromeCapabilities(
    profile?.role === "employer",
  )
  const role = useChromeRole(profile)
  const chromeOpts = { role }
  const showCandidate = usesCandidateChrome(pathname, chromeOpts)
  // Only wait when the path's chrome actually depends on role (e.g. /jobs
  // for seekers vs guests). Path-stable shells paint immediately.
  const chromePending =
    chromeDependsOnRole(pathname) &&
    !showCandidate &&
    (convexAuthLoading || profile === undefined) &&
    role === null

  const showMarketing =
    !chromePending && usesMarketingChrome(pathname, chromeOpts)
  const showApp = !chromePending && usesAppChrome(pathname, chromeOpts)

  const showEmployer = !chromePending && usesEmployerChrome(pathname, chromeOpts)
  const showAdmin = !chromePending && usesAdminChrome(pathname)

  return (
    <PostHogProvider client={posthog}>
      <CompanyCapabilitiesProvider value={companyCapabilities}>
      <div className="min-h-dvh flex flex-col bg-background text-foreground">
      {/*
        THESIS: One ink & lime portal — desks inherit the public website world; forest chrome stays legacy-only.
        OWN-WORLD: White ground, Sora, lime pills, frosted mkt-nav; desk links left beside the logo.
        STORY: Members operate hire/seek tasks inside the same brand they met on the marketing door.
        FIRST VIEWPORT: Sticky logo-left / links-left / actions-right nav, then mkt-page desk content.
        FORM: Established Ink & Lime Dual Door extended into Operate desks (code-led; recruit-plus-nav-right).
        FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, and DESIGN.md
      */}
      <AppShellFonts role={chromeOpts.role} />
      {chromePending ? <ChromePlaceholder /> : null}
      {showMarketing ? <MarketingHeader /> : null}
      {showApp ? (
        showAdmin ? (
          <AdminHeader />
        ) : showCandidate ? (
          <CandidateHeader />
        ) : showEmployer ? (
          <EmployerHeader
            canManageHackathons={companyCapabilities?.manageHackathons === true}
            workspaceAvailable={Boolean(companyCapabilities)}
          />
        ) : (
          <SiteHeader />
        )
      ) : null}
      <main className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </main>
      <Toaster richColors position="top-center" />
      </div>
      </CompanyCapabilitiesProvider>
    </PostHogProvider>
  )
}
